import { ArrowRight } from '@bb-ui/icons/dist/small/ArrowRight';
import { IconButton } from '@bb-ui/react-library/dist/components/IconButton';
import { Link } from '@bb-ui/react-library/dist/components/Link';
import { Typography } from '@bb-ui/react-library/dist/components/Typography';
import {
    useMediaQuery,
    useTheme,
} from '@bb-ui/react-library/dist/components/styles';
import React from 'react';
import { Trans, useTranslation } from 'react-i18next';

import { useAppNavigationContext } from '../../contexts/AppNavigationContext';
import { useStyles } from './DeveloperPage.styles';
import { DeveloperPageCard } from './DeveloperPageCard';

import { useApplicationTitle } from '../../hooks/useApplicationTitle';
import { useApi } from '../../hooks/useApi';
import { useHelpLinks } from '../../hooks/useHelpLinks';
import { useUser } from '../../hooks/useUser';
import { useAppConfigContext } from '../../contexts/AppConfigProvider';
import { ActionAreaButtons } from '../ActionAreaButtons/ActionAreaButtons';
import { LoadingIndicator } from '../LoadingIndicator/LoadingIndicator';
import { ResponsiveBox } from '../ResponsiveBox/ResponsiveBox';
import { SnowflakeLauncher } from '../SnowflakeLauncher/SnowflakeLauncher';
import { DeveloperPageHero } from './DeveloperPageHero';
import { useAccessControl } from '../../hooks/useAccessControl';
import { CopyButton } from '../CopyButton/CopyButton';

interface SnowflakeConnectionDetails {
    username?: string;
    accountIdentifier?: string;
    accountName?: string;
    accountUrl?: string;
    locatorUrl?: string;
    orgName?: string;
    warehouse?: string;
    databaseName?: string;
    roleName?: string;
    snowflakeUrl?: string;
}

interface ConnectionDetailsEnvelope {
    connectionDetails?: SnowflakeConnectionDetails;
}

interface ConnectionDetailRow {
    labelI18nKey: string;
    value: string | undefined;
    rowKey: string;
    valueTestId?: string;
    canCopy?: boolean;
}

const buildConnectionRows = ({
    details,
    sessionUsername,
    t,
}: {
    details: SnowflakeConnectionDetails | null;
    sessionUsername: string | undefined;
    t: (key: string) => string;
}) => {
    const accountUrl = details?.accountUrl?.toLowerCase();
    const sortRows = (rows: ConnectionDetailRow[]) =>
        [...rows].sort((a, b) =>
            t(a.labelI18nKey)
                .toLowerCase()
                .localeCompare(t(b.labelI18nKey).toLowerCase()),
        );

    const sharedRows = (
        prefix: 'service' | 'session',
    ): ConnectionDetailRow[] => [
        {
            labelI18nKey: 'developer.cards.connectionDetails.accountIdLabel',
            value: details?.accountIdentifier,
            rowKey: `${prefix}-account-id`,
        },
        {
            labelI18nKey: 'developer.cards.connectionDetails.orgNameLabel',
            value: details?.orgName,
            rowKey: `${prefix}-org-name`,
        },
        {
            labelI18nKey: 'developer.cards.connectionDetails.warehouseLabel',
            value: details?.warehouse,
            rowKey: `${prefix}-warehouse`,
        },
        {
            labelI18nKey: 'developer.cards.connectionDetails.databaseLabel',
            value: details?.databaseName,
            rowKey: `${prefix}-database`,
        },
        {
            labelI18nKey: 'developer.cards.connectionDetails.roleLabel',
            value: details?.roleName,
            rowKey: `${prefix}-role`,
        },
        {
            labelI18nKey: 'developer.cards.connectionDetails.urlLabel',
            value: accountUrl,
            rowKey: `${prefix}-url`,
        },
    ];

    const serviceMethodRows: ConnectionDetailRow[] = [
        {
            labelI18nKey: 'developer.cards.connectionDetails.usernameLabel',
            value: details?.username,
            rowKey: 'service-username',
        },
        {
            labelI18nKey: 'developer.cards.connectionDetails.passwordLabel',
            value: t(
                'developer.cards.connectionDetails.passwordFromAdminValue',
            ),
            rowKey: 'service-password',
            canCopy: false,
        },
        {
            labelI18nKey:
                'developer.cards.connectionDetails.authenticatorLabel',
            value: 'snowflake',
            rowKey: 'service-authenticator',
        },
    ];

    const sessionMethodRows: ConnectionDetailRow[] = [
        {
            labelI18nKey:
                'developer.cards.connectionDetails.sessionUsernameLabel',
            value: sessionUsername,
            rowKey: 'session-username',
            valueTestId: 'developer-page-connection-session-username',
        },
        {
            labelI18nKey: 'developer.cards.connectionDetails.passwordLabel',
            value: t('developer.cards.connectionDetails.notRequiredValue'),
            rowKey: 'session-password',
            canCopy: false,
        },
        {
            labelI18nKey:
                'developer.cards.connectionDetails.authenticatorLabel',
            value: 'externalbrowser',
            rowKey: 'session-authenticator',
        },
    ];

    const serviceRows = sortRows([
        ...sharedRows('service'),
        ...serviceMethodRows,
    ]);

    const sessionRows = sortRows([
        ...sharedRows('session'),
        ...sessionMethodRows,
    ]);

    return { serviceRows, sessionRows };
};

export const DeveloperPage: React.FunctionComponent = () => {
    const { t } = useTranslation();
    const classes = useStyles();
    const { openSignInModal } = useAppNavigationContext();
    const getHelpLink = useHelpLinks();
    const user = useUser();
    const { api } = useAppConfigContext();
    const isAuthenticated = user !== null;
    const access = useAccessControl();
    const [isConnectionDetailsVisible, setIsConnectionDetailsVisible] =
        React.useState(false);
    const [hasRequestedConnectionDetails, setHasRequestedConnectionDetails] =
        React.useState(false);
    // This does not get localized
    useApplicationTitle('Developer');

    const theme = useTheme();

    const smDown = useMediaQuery(theme.breakpoints.down('sm'));
    const mdBreakpoint = useMediaQuery(theme.breakpoints.only('md'));

    const { openNavDrawer } = useAppNavigationContext();

    const bannerOrientation =
        smDown || (mdBreakpoint && openNavDrawer === 'global')
            ? 'vertical'
            : 'horizontal';

    const dataDictionaryCardId = 'data-dictionary';
    const manageSnowflakeCardId = 'manage-snowflake';
    const dataExplorersCardId = 'data-explorers';
    const directDataAccessCardId = 'direct-data-access';
    const sessionUsername = user?.userId;

    const {
        data: connectionDetailsResponse,
        loading: connectionDetailsLoading,
        error: connectionDetailsError,
        execute: executeConnectionDetailsRequest,
    } = useApi<ConnectionDetailsEnvelope>({
        executeType: 'manual',
        useAuth: true,
        method: 'GET',
    });

    const connectionDetails =
        connectionDetailsResponse?.connectionDetails || null;
    const showConnectionDetailsLoading =
        hasRequestedConnectionDetails && connectionDetailsLoading;
    const showConnectionDetailsError =
        hasRequestedConnectionDetails &&
        !connectionDetailsLoading &&
        (Boolean(connectionDetailsError) || !connectionDetails);

    const {
        serviceRows: serviceConnectionRows,
        sessionRows: sessionConnectionRows,
    } = React.useMemo(
        () =>
            buildConnectionRows({
                details: connectionDetails,
                sessionUsername,
                t,
            }),
        [connectionDetails, sessionUsername, t],
    );

    const renderConnectionDetailRow = (
        labelI18nKey: string,
        value: string | undefined,
        rowKey: string,
        valueTestId?: string,
        canCopy = true,
    ) => {
        const normalizedValue = value || '';
        const hasValue = normalizedValue.length > 0;

        return (
            <React.Fragment key={rowKey}>
                <Typography
                    variant="body2"
                    component="p"
                    className={classes.connectionLabel}
                >
                    {t(labelI18nKey)}
                </Typography>
                <Typography
                    variant="body2"
                    component="p"
                    className={classes.connectionValue}
                    data-testid={valueTestId}
                >
                    {value}
                </Typography>
                {canCopy && hasValue ? (
                    <CopyButton
                        iconOnly
                        text={normalizedValue}
                        label={t(
                            'developer.cards.connectionDetails.copyAriaLabel',
                            {
                                field: t(labelI18nKey),
                            },
                        )}
                        testId={`developer-page-connection-copy-${rowKey}`}
                        className={classes.copyCell}
                        statusClassName={classes.copyStatusRow}
                    />
                ) : (
                    <>
                        <div className={classes.copyColumnPlaceholder} />
                        <div
                            className={classes.connectionRowDivider}
                            aria-hidden="true"
                        />
                    </>
                )}
            </React.Fragment>
        );
    };

    const renderConnectionDetailRows = (rows: ConnectionDetailRow[]) =>
        rows.map(row =>
            renderConnectionDetailRow(
                row.labelI18nKey,
                row.value,
                row.rowKey,
                row.valueTestId,
                row.canCopy ?? true,
            ),
        );

    const dataDictionaryCard = (
        <DeveloperPageCard
            id={dataDictionaryCardId}
            title={t('developer.cards.dataDictionary.title')}
            link={{
                text: t('developer.cards.dataDictionary.linkText'),
                to: '/dictionary',
            }}
        >
            <Typography
                variant="body1"
                data-testid={`developer-page-${dataDictionaryCardId}-card-text`}
            >
                { /* prettier-ignore */ }
                <Trans i18nKey='developer.cards.dataDictionary.text'>
                    Explore the data dictionary to learn about the structure and content of the <span lang="en">Blackboard Illuminate</span> model.
                </Trans>
            </Typography>
        </DeveloperPageCard>
    );

    const manageSnowflakeCard = (
        <DeveloperPageCard
            id={manageSnowflakeCardId}
            title={
                /* prettier-ignore */
                <Trans i18nKey="developer.cards.manageSnowflake.title">
                    Manage your <span lang="en">Snowflake</span> service accounts
                </Trans>
            }
            linkReplacement={
                !access['developer/settings'].ok ? (
                    <IconButton
                        className={classes.linkButton}
                        onClick={openSignInModal}
                    >
                        <Typography
                            component="span"
                            variant="subtitle2"
                            className={classes.linkButtonText}
                        >
                            {t(
                                'developer.cards.manageSnowflake.linkTextUnauthenticated',
                            )}
                        </Typography>
                        <ArrowRight role="presentation" />
                    </IconButton>
                ) : undefined
            }
            link={
                isAuthenticated
                    ? {
                          text: t(
                              'developer.cards.manageSnowflake.linkTextAuthenticated',
                          ),
                          to: '/settings',
                      }
                    : undefined
            }
            secondaryAction={
                isAuthenticated &&
                access['developer/settings'].ok &&
                access['developer/launch-snowflake'].ok ? (
                    <Link
                        component="button"
                        onClick={() => {
                            setIsConnectionDetailsVisible(true);
                            setHasRequestedConnectionDetails(true);
                            if (
                                !connectionDetailsLoading &&
                                !connectionDetails &&
                                api?.serviceAccountManagement &&
                                user?.tenantIdentifier
                            ) {
                                executeConnectionDetailsRequest({
                                    url: `${api.serviceAccountManagement}/v1/data/tenants/${user.tenantIdentifier}/serviceAccounts/connectionDetails`,
                                });
                            }
                        }}
                        className={classes.howToConnectLink}
                        data-testid="developer-page-manage-snowflake-how-to-connect"
                    >
                        {t('developer.cards.manageSnowflake.howToConnect')}
                    </Link>
                ) : undefined
            }
        >
            <ul className={classes.unorderedList}>
                <Typography component="li" variant="body1">
                    {t('developer.cards.manageSnowflake.text1')}
                </Typography>
                <Typography component="li" variant="body1">
                    {t('developer.cards.manageSnowflake.text2')}
                </Typography>
                <Typography component="li" variant="body1">
                    {t('developer.cards.manageSnowflake.text3')}
                </Typography>
            </ul>
        </DeveloperPageCard>
    );

    const snowflakeConnectionDetailsCard =
        access['developer/launch-snowflake'].ok &&
        isConnectionDetailsVisible ? (
            <DeveloperPageCard
                id="snowflake-connection-details"
                title={
                    <Trans i18nKey="developer.cards.connectionDetails.title">
                        How to connect to <span lang="en">Snowflake</span>
                    </Trans>
                }
                className={classes.fullWidthCard}
            >
                {showConnectionDetailsLoading && (
                    <div className={classes.connectionDetailsLoading}>
                        <LoadingIndicator
                            id="loading-connection-details"
                            label={t(
                                'developer.cards.connectionDetails.loading',
                            )}
                        />
                    </div>
                )}
                {showConnectionDetailsError && (
                    <Typography
                        variant="body2"
                        component="p"
                        className={classes.errorText}
                    >
                        {t('developer.cards.connectionDetails.loadError')}
                    </Typography>
                )}
                {!showConnectionDetailsLoading &&
                    !showConnectionDetailsError && (
                        <>
                            <div className={classes.connectionMethodsRow}>
                                <div className={classes.connectionMethodCard}>
                                    <Typography
                                        variant="subtitle1"
                                        component="h3"
                                        className={
                                            classes.connectionMethodHeading
                                        }
                                    >
                                        {t(
                                            'developer.cards.connectionDetails.serviceMethodTitle',
                                        )}
                                    </Typography>
                                    <Typography
                                        variant="body2"
                                        component="p"
                                        className={
                                            classes.connectionMethodDescription
                                        }
                                    >
                                        {t(
                                            'developer.cards.connectionDetails.serviceMethodDescription',
                                        )}
                                    </Typography>
                                    <div
                                        className={
                                            classes.connectionDetailsScrollBox
                                        }
                                    >
                                        <div
                                            className={
                                                classes.connectionDetailsGrid
                                            }
                                        >
                                            {renderConnectionDetailRows(
                                                serviceConnectionRows,
                                            )}
                                        </div>
                                    </div>
                                </div>

                                <div className={classes.connectionMethodCard}>
                                    <Typography
                                        variant="subtitle1"
                                        component="h3"
                                        className={
                                            classes.connectionMethodHeading
                                        }
                                    >
                                        {t(
                                            'developer.cards.connectionDetails.sessionMethodTitle',
                                        )}
                                    </Typography>
                                    <Typography
                                        variant="body2"
                                        component="p"
                                        className={
                                            classes.connectionMethodDescription
                                        }
                                    >
                                        {t(
                                            'developer.cards.connectionDetails.sessionMethodDescription',
                                        )}
                                    </Typography>
                                    <div
                                        className={
                                            classes.connectionDetailsScrollBox
                                        }
                                    >
                                        <div
                                            className={
                                                classes.connectionDetailsGrid
                                            }
                                        >
                                            {renderConnectionDetailRows(
                                                sessionConnectionRows,
                                            )}
                                        </div>
                                    </div>
                                </div>
                            </div>
                            <div className={classes.additionalResourcesSection}>
                                <Typography
                                    variant="subtitle1"
                                    component="h3"
                                    className={classes.connectionMethodHeading}
                                >
                                    {t(
                                        'developer.cards.connectionDetails.additionalResourcesTitle',
                                    )}
                                </Typography>
                                <Link
                                    href="https://docs.snowflake.com/en/user-guide/gen-conn-config"
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    data-testid="developer-page-connection-additional-resources-link"
                                >
                                    {t(
                                        'developer.cards.connectionDetails.additionalResourcesLinkText',
                                    )}
                                </Link>
                            </div>
                        </>
                    )}
            </DeveloperPageCard>
        ) : null;

    const dataExplorersCard = (
        <DeveloperPageCard
            id={dataExplorersCardId}
            title={
                /* prettier-ignore */
                <Trans i18nKey="developer.cards.dataExplorers.title">
                    Grow your <span lang="en">SQL</span> skills and more
                </Trans>
            }
            link={{
                text:
                    /* prettier-ignore */
                    <Trans i18nKey="developer.cards.dataExplorers.linkText">
                        Go to <span lang="en">Blackboard Data Explorers</span>
                    </Trans>,
                to: 'https://data-explorers.blackboard.com',
                isExternal: true,
            }}
            image={{
                url: '/images/DeveloperPage/dataExplorersIllustration.svg',
                position: 'left',
                objectPosition: 'left bottom',
            }}
        >
            <Typography data-testid="developer-cards-data-explorers-text-heading">
                {/* prettier-ignore */}
                <Trans i18nKey="developer.cards.dataExplorers.text1">
                    <span lang="en" className={classes.explorersName}>Blackboard Data Explorers</span> is an interactive course designed to:
                </Trans>
            </Typography>
            <ol className={classes.orderedList}>
                <Typography
                    component="li"
                    data-testid={`developer-page-${dataExplorersCardId}-card-text1`}
                >
                    {/* prettier-ignore */}
                    <Trans i18nKey="developer.cards.dataExplorers.text2">
                        Get those without any <span lang="en">SQL</span> skill to make use of <span lang="en">Blackboard Illuminate</span> Developer.
                    </Trans>
                </Typography>
                <Typography
                    component="li"
                    data-testid={`developer-page-${dataExplorersCardId}-card-text2`}
                >
                    {/* prettier-ignore */}
                    <Trans i18nKey="developer.cards.dataExplorers.text3">
                        Enable <span lang="en">SQL</span>-skilled developers to apply institutional context to the data.
                    </Trans>
                </Typography>
            </ol>
        </DeveloperPageCard>
    );

    const directDataAccessCard = (
        <DeveloperPageCard
            id={directDataAccessCardId}
            title={
                /* prettier-ignore */
                <Trans i18nKey="developer.cards.directDataAccess.title">
                    What about <span lang="en">Direct Data Access (DDA)</span>?
                </Trans>
            }
            link={{
                text:
                    /* prettier-ignore */
                    <Trans i18nKey="developer.cards.directDataAccess.linkText">
                        Learn more about <span lang="en">DDA</span>
                    </Trans>,
                to: getHelpLink('directDataAccess'),
                isExternal: true,
            }}
            image={{
                url: '/images/DeveloperPage/DDAIllustration.svg',
                position: 'right',
                objectPosition: 'right bottom',
            }}
        >
            <Typography
                data-testid={`developer-page-${directDataAccessCardId}-card-text1`}
            >
                {/* prettier-ignore */}
                <Trans i18nKey="developer.cards.directDataAccess.text1">
                    <span lang="en">DDA</span> is a copy of your institution's <span lang="en">Blackboard Learn</span> transactional database. It's included with some packages of <span lang="en">Learn SaaS</span> and available as an additional purchase for others.
                </Trans>
            </Typography>
            <Typography
                data-testid={`developer-page-${directDataAccessCardId}-card-text2`}
            >
                {/* prettier-ignore */}
                <Trans i18nKey="developer.cards.directDataAccess.text2">
                    <span lang="en">DDA</span> and <span lang="en">Blackboard Illuminate</span> Developer differ in their source, structure and freshness.
                </Trans>
            </Typography>
        </DeveloperPageCard>
    );

    const accessInstitutionalDataCardWhenNotAuthorized = (
        <DeveloperPageCard
            id="access-institutional-data"
            title={t('developer.cards.accessInstitutionalData.title')}
            link={{
                text: t('developer.getDeveloper'),
                to: getHelpLink('getReportingHelp'),
                isExternal: true,
            }}
        >
            <Typography data-testid="developer-page-access-institution-data-text-when-not-authorized">
                { /* prettier-ignore */ }
                <Trans i18nKey="developer.cards.accessInstitutionalData.textWhenNotAuthorized">
                    <span lang="en">Blackboard Illuminate</span> Developer gives you access to your institutional data through <span lang="en">Snowflake</span>. With <span lang="en">Snowflake</span> you can run <span lang="en">SQL</span> queries and analyses based on your institutional needs.
                </Trans>
            </Typography>
        </DeveloperPageCard>
    );

    const accessInstitutionalDataCardWhenAuthorized = (
        <DeveloperPageCard
            id="access-institutional-data"
            title={t('developer.cards.accessInstitutionalData.title')}
            linkReplacement={
                <Link
                    href={getHelpLink('getReportingHelp')}
                    target="_blank"
                    rel="noopener noreferrer"
                >
                    {t('developer.cards.accessInstitutionalData.linkText')}
                </Link>
            }
        >
            <SnowflakeLauncher />
        </DeveloperPageCard>
    );

    const getCards = () => {
        const cards = [dataDictionaryCard];

        if (isAuthenticated) {
            if (access['developer/launch-snowflake'].ok) {
                // Put this card at the beginning
                cards.unshift(accessInstitutionalDataCardWhenAuthorized);
            } else {
                // Put this card at the beginning
                cards.unshift(accessInstitutionalDataCardWhenNotAuthorized);
            }
            if (access['developer/settings'].ok) {
                cards.push(manageSnowflakeCard);
            }
        } else {
            cards.push(manageSnowflakeCard);
        }

        if (cards.length === 2) {
            return <div className={classes.twoCards}>{...cards}</div>;
        }

        if (cards.length === 3) {
            return <div className={classes.threeCards}>{...cards}</div>;
        }
    };

    return (
        <ResponsiveBox
            className={classes.developerPageContainer}
            data-testid="developer-page-container"
        >
            <div className={classes.gridContainer}>
                <DeveloperPageHero orientation={bannerOrientation} />
                {getCards()}
                {snowflakeConnectionDetailsCard}
                {dataExplorersCard}
                {directDataAccessCard}
            </div>
            <ActionAreaButtons helpLink={getHelpLink('developer')} />
        </ResponsiveBox>
    );
};
